import React from 'react';
import tw from 'twin.macro';
import Select from '@teetsh/app/src/components/Form/Select2';
import styled from '@emotion/styled';

import { ReactComponent as CourierPrimeMontserrat } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/courier_prime-montserrat.svg';
import { ReactComponent as ArvoLato } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/arvo-lato.svg';
import { ReactComponent as MaliRaleway } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/mali-raleway.svg';
import { ReactComponent as JostMerriweather } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/jost-merriweather.svg';
import { ReactComponent as JosefinSansOpenSans } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/josefinsans-open_sans.svg';
import { ReactComponent as GeologicaInter } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/geologica-inter.svg';
import { ReactComponent as PatrickHandNunito } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/patrick_hand-nunito.svg';
import { ReactComponent as IndieFlowerAssistant } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/indie_flower-assistant.svg';
import { ReactComponent as SacramentoRoboto } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/sacramento-roboto.svg';
import { ReactComponent as PacificoOpenSans } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/pacifico-open_sans.svg';
import { ReactComponent as LoraTimesNewRoman } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/lora-times_new_roman.svg';
import { ReactComponent as DMSerifTextDMSans } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/dm_serif_text-dm_sans.svg';
import { ReactComponent as ParisienneLora } from '@teetsh/app/src/components/Form/FontsCoupleSelector/assets/parisienne-lora.svg';
import { useTranslation } from 'react-i18next';

const FONTS_CATEGORIES = [
  'simple',
  'childish',
  'classic',
  'elegant',
  'other',
] as const;
type FontCategory = (typeof FONTS_CATEGORIES)[number];

interface FontCoupleOption {
  label: string;
  value: string;
  preview: React.FC<any>;
  headingFontFamily: string;
  bodyFontFamily: string;
  category?: FontCategory;
}

export const FONTS_COUPLES: FontCoupleOption[] = [
  {
    label: 'Geologica & Inter',
    value: 'geologica',
    preview: GeologicaInter,
    headingFontFamily: 'Geologica, sans-serif',
    bodyFontFamily: 'Inter, sans-serif',
    category: 'simple',
  },
  {
    label: 'Jost & Merriweather',
    value: 'jost',
    preview: JostMerriweather,
    headingFontFamily: 'Jost, sans-serif',
    bodyFontFamily: 'Merriweather, serif',
    category: 'simple',
  },
  {
    label: 'Josefin Sans & Open Sans',
    value: 'josefinsans',
    preview: JosefinSansOpenSans,
    headingFontFamily: 'Josefin Sans, sans-serif',
    bodyFontFamily: 'Open Sans, sans-serif',
    category: 'simple',
  },
  {
    label: 'Patrick Hand & Nunito',
    value: 'patrickhand',
    preview: PatrickHandNunito,
    headingFontFamily: 'Patrick Hand, cursive',
    bodyFontFamily: 'Nunito, sans-serif',
    category: 'childish',
  },
  {
    label: 'Indie Flower & Assistant',
    value: 'indieflower',
    preview: IndieFlowerAssistant,
    headingFontFamily: 'Indie Flower, cursive',
    bodyFontFamily: 'Assistant, sans-serif',
    category: 'childish',
  },
  {
    label: 'Sacramento & Roboto',
    value: 'sacramento',
    preview: SacramentoRoboto,
    headingFontFamily: 'Sacramento, cursive',
    bodyFontFamily: 'Roboto, sans-serif',
    category: 'childish',
  },
  {
    label: 'Pacifico & Open Sans',
    value: 'pacifico',
    preview: PacificoOpenSans,
    headingFontFamily: 'Pacifico, cursive',
    bodyFontFamily: 'Open Sans, sans-serif',
    category: 'childish',
  },
  {
    label: 'Lora & Times New Roman',
    value: 'lora',
    preview: LoraTimesNewRoman,
    headingFontFamily: 'Lora, serif',
    bodyFontFamily: 'Times New Roman, serif',
    category: 'classic',
  },
  {
    label: 'DM Serif Text & DM Sans',
    value: 'dmseriftext',
    preview: DMSerifTextDMSans,
    headingFontFamily: 'DM Serif Text, serif',
    bodyFontFamily: 'DM Sans, sans-serif',
    category: 'classic',
  },
  {
    label: 'Parisienne & Lora',
    value: 'parisienne',
    preview: ParisienneLora,
    headingFontFamily: 'Parisienne, cursive',
    bodyFontFamily: 'Lora, serif',
    category: 'elegant',
  },
  {
    label: 'Courier Prime & Montserrat',
    value: 'courier',
    preview: CourierPrimeMontserrat,
    headingFontFamily: 'Courier Prime, serif',
    bodyFontFamily: 'Montserrat, serif',
    category: 'other',
  },
  {
    label: 'Arvo & Lato',
    value: 'lato',
    preview: ArvoLato,
    headingFontFamily: 'Arvo, serif',
    bodyFontFamily: 'Lato, sans-serif',
    category: 'other',
  },
  {
    label: 'Mali & Raleway',
    value: 'mali',
    preview: MaliRaleway,
    headingFontFamily: 'Mali, cursive',
    bodyFontFamily: 'Raleway, sans-serif',
    category: 'other',
  },
] as const;

export function getFontCouples(font: string) {
  const { headingFontFamily, bodyFontFamily } = FONTS_COUPLES.find(
    opt => opt.value === font,
  ) || {
    headingFontFamily: 'serif',
    bodyFontFamily: 'sans-serif',
  };
  return {
    headingFontFamily,
    bodyFontFamily,
  };
}

const DoubleFontPreview = styled.div`
  ${tw`h-full min-h-8`}
`;

interface Props {
  value: string;
  onChange: (v: string) => void;
}

export function FontsCoupleSelector(props: Props) {
  const { t } = useTranslation('userSettings');
  return (
    <Select<string>
      {...props}
      tw="min-w-[200px] h-[4.5rem] overflow-hidden"
      options={[...new Set(FONTS_COUPLES.map(opt => opt.category))].flatMap(
        cat => [
          {
            label: t(`fonts.category.${cat || 'other'}`),
            group: true as const,
          },
          ...FONTS_COUPLES.filter(opt => opt.category === cat).map(
            ({ value, label, preview }) => ({
              value,
              label: (
                <div>
                  <div tw="mb-1 whitespace-normal">{label}</div>
                  <DoubleFontPreview as={preview} />
                </div>
              ),
            }),
          ),
        ],
      )}
    />
  );
}
